import dayjs from "dayjs";

/** Shown where a timestamp is absent, e.g. a run that has not started yet. */
const ABSENT = "—";

/**
 * Formats a run timestamp for the history table.
 *
 * The `DateTime` scalar arrives untyped, so anything dayjs cannot read is
 * rendered as absent rather than as "Invalid Date".
 *
 * @param value The timestamp from the run view.
 * @returns A `YYYY-MM-DD HH:mm` string, or an em dash when there is no usable
 *   timestamp.
 */
export const formatRunTimestamp = (value: unknown): string => {
  if (typeof value !== "string" && !(value instanceof Date)) {
    return ABSENT;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : ABSENT;
};

/**
 * Formats how long a run took, for the history table.
 *
 * @param startedAt When the worker picked the run up.
 * @param finishedAt When it finished.
 * @returns A whole-second or whole-minute duration, or an em dash when either
 *   end is missing or unreadable.
 */
export const formatRunDuration = (
  startedAt: unknown,
  finishedAt: unknown,
): string => {
  if (
    (typeof startedAt !== "string" && !(startedAt instanceof Date)) ||
    (typeof finishedAt !== "string" && !(finishedAt instanceof Date))
  ) {
    return ABSENT;
  }
  const start = dayjs(startedAt);
  const end = dayjs(finishedAt);
  if (!start.isValid() || !end.isValid()) {
    return ABSENT;
  }
  const seconds = end.diff(start, "second");
  if (seconds < 0) {
    return ABSENT;
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  return `${Math.round(seconds / 60)}m`;
};
